import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UserIcon, ShieldIcon, BellIcon, HelpCircleIcon, InfoIcon, LogOutIcon, XIcon, ChevronRightIcon } from 'lucide-react';
import { useTranslation } from '../utils/translations';
interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onLogout?: () => void;
}
export function Sidebar({
  isOpen,
  onClose,
  onLogout
}: SidebarProps) {
  const {
    t,
    language,
    setLanguage,
    isRtl
  } = useTranslation();
  const menuItems = [{
    icon: UserIcon,
    label: t('accountSettings'),
    color: '#661806'
  }, {
    icon: ShieldIcon,
    label: t('security'),
    color: '#10B981'
  }, {
    icon: BellIcon,
    label: t('notifications'),
    color: '#F37024'
  }, {
    icon: HelpCircleIcon,
    label: t('helpSupport'),
    color: '#3B82F6'
  }, {
    icon: InfoIcon,
    label: t('about'),
    color: '#8B5CF6'
  }, {
    icon: LogOutIcon,
    label: t('logout'),
    color: '#EF4444',
    onClick: onLogout
  }];
  return <AnimatePresence data-id="element-963">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/50 z-[100] backdrop-blur-sm" data-id="element-964" />

          {/* Sidebar */}
          <motion.div initial={{
        x: isRtl ? '100%' : '-100%'
      }} animate={{
        x: 0
      }} exit={{
        x: isRtl ? '100%' : '-100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className={`fixed top-0 bottom-0 ${isRtl ? 'right-0' : 'left-0'} w-[280px] bg-[#FFF7F3] z-[101] shadow-2xl flex flex-col overflow-hidden`} data-id="element-965">
            {/* Header / Profile Section */}
            <div className="bg-gradient-to-br from-[#661806] to-[#D95A1A] p-6 pt-12 pb-8 relative" data-id="element-966">
              <button onClick={onClose} className={`absolute top-6 ${isRtl ? 'left-6' : 'right-6'} text-white/80 hover:text-white`} data-id="element-967">
                <XIcon className="w-6 h-6" strokeWidth={2} data-id="element-968" />
              </button>
              <div className="flex items-center gap-4" data-id="element-969">
                <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center text-[#F37024] text-xl font-bold shadow-md" data-id="element-970">
                  K
                </div>
                <div data-id="element-971">
                  <h3 className="text-lg font-bold text-white" data-id="element-972">Kashish</h3>
                  <p className="text-sm text-white/80" dir="ltr" data-id="element-973">
                    +92 302 2746326
                  </p>
                </div>
              </div>
            </div>

            {/* Menu Items */}
            <div className="flex-1 overflow-y-auto py-4" data-id="element-974">
              <div className="px-4 space-y-1" data-id="element-975">
                {menuItems.map((item, index) => {
              const Icon = item.icon;
              return <button key={index} onClick={item.onClick} className="w-full px-4 py-3.5 flex items-center gap-4 hover:bg-white rounded-xl transition-colors group" data-id="element-976">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110" style={{
                  backgroundColor: `${item.color}15`
                }} data-id="element-977">
                        <Icon className="w-5 h-5" style={{
                    color: item.color
                  }} strokeWidth={2} data-id="element-978" />
                      </div>
                      <span className="flex-1 text-left text-[15px] font-semibold text-[#303030]" data-id="element-979">
                        {item.label}
                      </span>
                      <ChevronRightIcon className={`w-5 h-5 text-[#818285] ${isRtl ? 'rotate-180' : ''}`} strokeWidth={2} data-id="element-980" />
                    </button>;
            })}
              </div>
            </div>

            {/* Footer Section */}
            <div className="p-6 bg-white border-t border-[#F0E8E3]" data-id="element-981">
              {/* Language Toggle */}
              <div className="flex items-center justify-between mb-6" data-id="element-982">
                <span className="text-[14px] font-semibold text-[#303030]" data-id="element-983">
                  {t('language')}
                </span>
                <div className="flex bg-[#F5EBE6] rounded-full p-1" data-id="element-984">
                  <button onClick={() => setLanguage('en')} className={`px-4 py-1.5 rounded-full text-[13px] font-bold transition-all ${language === 'en' ? 'bg-[#661806] text-white shadow-sm' : 'text-[#818285] hover:text-[#303030]'}`} data-id="element-985">
                    EN
                  </button>
                  <button onClick={() => setLanguage('ur')} className={`px-4 py-1.5 rounded-full text-[13px] font-bold font-urdu transition-all ${language === 'ur' ? 'bg-[#661806] text-white shadow-sm' : 'text-[#818285] hover:text-[#303030]'}`} data-id="element-986">
                    اردو
                  </button>
                </div>
              </div>

              {/* Version Info */}
              <div className="text-center" data-id="element-987">
                <p className="text-[12px] font-bold text-[#818285]" data-id="element-988">
                  digiBOP v2.1.0
                </p>
                <p className="text-[11px] text-[#818285]/70 mt-0.5" data-id="element-989">
                  © 2025 Bank of Punjab
                </p>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}